import { useState } from "react";
import { ChevronDown, ChevronRight, BarChart3 } from "lucide-react";

const VesselEmissionOverview = () => {
  const [openSections, setOpenSections] = useState(false);

  const toggleSection = () => {
    setOpenSections((prev) => !prev);
  };

  const emissionOverview = [
    {
      id: 1,
      title: "Carbon Dioxide (CO₂)",
      formula: "CO₂ (MT/day) = Σ(Daily Fuel (t) × CF_CO₂) / 1000",
      calculation: "For each fuel type: (HFO_daily_t × CF_C02_HFO) + (LFO_daily_t × CF_C02_LFO) + (MGO_daily_t × CF_C02_MGO)",
      calculationDetails: [
        "Daily fuel consumption from VesselPerformance records",
        "CF_C02 values from FuelTable for each fuel type",
        "Calculated per day across selected time period",
        "Result in Metric tons per day (MT/day)",
        "Target: From Company Management in Admin Panel"
      ],
      description: "Daily CO₂ emissions calculated from fuel consumption using fuel-specific emission factors.",
      borderColor: "border-blue-200",
      bgColor: "bg-blue-50",
      textColor: "text-blue-700",
    },
    {
      id: 2,
      title: "Nitrogen Oxides (NOₓ)",
      formula: "NOₓ (MT/day) = Σ(Fuel Consumption × NOₓ Factor) / 1000",
      calculation: "Based on engine type, operational mode, and specific NOₓ emission factors",
      calculationDetails: [
        "Main Engine HSFO/LSFO: 78.46 g/kg fuel",
        "Main Engine MDO/LSMDO: 73.75 g/kg fuel",
        "Aux Engine HSFO/LSFO: 49.37 g/kg fuel",
        "Aux Engine MDO/LSMDO: 46.37 g/kg fuel",
        "Boiler HSFO/LSFO: 6.89 g/kg fuel",
        "Boiler MDO/LSMDO: 6.47 g/kg fuel",
        "Target: From Company Management in Admin Panel"
      ],
      description: "Daily NOₓ emissions calculated using comprehensive engine-specific and mode-specific factors from vessel emission data.",
      borderColor: "border-green-200",
      bgColor: "bg-green-50",
      textColor: "text-green-700",
    },
    {
      id: 3,
      title: "Sulfur Oxides (SOₓ)",
      formula: "SOₓ (MT/day) = [Σ(Fuel × Sulfur %) × 2 × 0.97753] / 100 / 1000",
      calculation: "Sum of (FuelConsHSFO × SulHSFO) + (FuelConsLSFO × SulLSFO) + (FuelConsMDO × SulMDO) + (FuelConsLSMDO × SulLSMDO) with conversion",
      calculationDetails: [
        "Conversion factor: 2 × 0.97753 converts sulfur to SO₂",
        "Divided by 100 to convert % to decimal",
        "Divided by 1000 to convert kg to MT",
        "Uses sulfur content from vessel emission raw data",
        "Target: From Company Management in Admin Panel"
      ],
      description: "Daily SOₓ emissions calculated from actual fuel sulfur content and consumption data.",
      borderColor: "border-amber-200",
      bgColor: "bg-amber-50",
      textColor: "text-amber-700",
    },
    {
      id: 4,
      title: "Particulate Matter (PM)",
      formula: "PM (MT/day) = Σ[(Daily Fuel / SFOC) × PM10_Kwh] / 1000",
      calculation: "PM10_Kwh = 1.35 + SFOC × 7 × 0.02247 × (Sulfur %/100 - 0.0246)",
      calculationDetails: [
        "Uses SFOC (Specific Fuel Oil Consumption) values from SFOC table",
        "SFOC for HFO/LFO: from HFO SSD data (~200+ g/kWh)",
        "SFOC for MGO: from MDO SSD data",
        "Sulfur content from latest vessel emission report",
        "Target: From Company Management in Admin Panel"
      ],
      description: "Daily PM emissions calculated using SFOC-based formula with sulfur dependency for different fuel types.",
      borderColor: "border-purple-200",
      bgColor: "bg-purple-50",
      textColor: "text-purple-700",
    },
    {
      id: 5,
      title: "Methane (CH₄)",
      formulas: [
        "CH₄ (MT/day) = Σ(Daily Fuel (t) × CF_CH₄) / 1000"
      ],
      calculation: "Fuel type using CF_CH4 factors from FuelTable",
      calculationDetails: [
        "HFO: Uses CF_CH4_HFO and CF_N20_HFO",
        "LFO: Uses CF_CH4_LFO and CF_N20_LFO",
        "MGO: Uses CF_CH4_MGO and CF_N20_MGO",
        "Target: From Company Management in Admin Panel"
      ],
      description: "Daily CH₄ and N₂O emissions calculated using fuel-specific emission factors from FuelTable.",
      borderColor: "border-teal-200",
      bgColor: "bg-teal-50",
      textColor: "text-teal-700",
    },
    {
      id: 6,
      title: "Nitrous Oxide (N₂O)",
      formulas: [
        "N₂O (MT/day) = Σ(Daily Fuel (t) × CF_N₂O) / 1000"
      ],
      calculation: "Fuel type using CF_N20 factors from FuelTable",
      calculationDetails: [
        "HFO: Uses CF_CH4_HFO and CF_N20_HFO",
        "LFO: Uses CF_CH4_LFO and CF_N20_LFO",
        "MGO: Uses CF_CH4_MGO and CF_N20_MGO",
        "Target: From Company Management in Admin Panel"
      ],
      description: "Daily CH₄ and N₂O emissions calculated using fuel-specific emission factors from FuelTable.",
      borderColor: "border-teal-200",
      bgColor: "bg-teal-50",
      textColor: "text-teal-700",
    },
    {
      id: 7,
      title: "CO₂ Equivalent (CO₂e) - Tank-to-Wake",
      formula: "CO₂e (MT/day) = CO₂ + (CH₄ × 28) + (N₂O × 265)",
      calculation: "Sum of all greenhouse gases weighted by GWP factors",
      calculationDetails: [
        "Global Warming Potential factors:",
        "CH₄: 28 times stronger than CO₂",
        "N₂O: 265 times stronger than CO₂ (Tank-to-Wake)",
        "Represents combustion emissions only",
        "Target: From Company Management in Admin Panel"
      ],
      description: "Total climate impact of combustion emissions expressed as CO₂ equivalent (Tank-to-Wake).",
      additionalInfo: "Note: Uses N₂O × 265 for Tank-to-Wake calculation (different from Well-to-Wake N₂O × 298).",
      borderColor: "border-rose-200",
      bgColor: "bg-rose-50",
      textColor: "text-rose-700",
    },
    {
      id: 8,
      title: "GHG Intensity - Well-to-Wake",
      formula: "GHG Intensity (g CO₂e/MJ) = Reward Factor × (WtT + TtW)",
      calculation: "Well-to-Tank (WtT) + Tank-to-Wake (TtW) emissions per unit of energy",
      calculationDetails: [
        "WtT: Emissions from fuel production and transport",
        "TtW: Emissions from fuel combustion",
        "Reward Factor: Currently 1.0",
        "Energy: Based on fuel LCV (Lower Calorific Value)",
        "Target: From Company Management in Admin Panel"
      ],
      description: "Complete lifecycle emissions intensity including fuel production, transport, and combustion.",
      additionalInfo: "Well-to-Wake intensity provides complete lifecycle assessment per unit of energy consumed.",
      borderColor: "border-indigo-200",
      bgColor: "bg-indigo-50",
      textColor: "text-indigo-700",
    },
  ];

  const fuelFactorsInfo = {
    title: "Fuel Data & Emission Factors",
    description: "Emission calculations use comprehensive fuel data from multiple sources:",
    sources: [
      {
        name: "FuelTable",
        factors: "CF_C02, CF_CH4, CF_N20, LCV, CO2eq_WtT, CSlip, Fuel_Class",
        purpose: "Fuel-specific emission factors and energy content"
      },
      {
        name: "SFocValue",
        factors: "2001+ SFOC values for HFO and MDO fuels",
        purpose: "Specific Fuel Oil Consumption for PM calculations"
      },
      {
        name: "Vessel.fuelTypes",
        factors: "HFO, LFO, MGO (vessel-specific fuel configuration)",
        purpose: "Determines which fuel types to include in calculations"
      }
    ]
  };

  return (
    <div>
      <div className="mb-6 border border-gray-300 rounded-lg overflow-hidden">
        <button
          className="flex items-center justify-between w-full p-5 bg-gray-50 hover:bg-gray-100 transition-colors"
          onClick={toggleSection}
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-[#c5fcdd] rounded-lg">
              <BarChart3 className="w-6 h-6 text-[#27ae60]" />
            </div>
            <div className="text-left">
              <h2 className="text-md font-bold text-gray-800">
                Vessel Emission Overview
              </h2>
              <p className="text-gray-600 text-sm">
                Daily time series emission calculations and target comparisons
              </p>
            </div>
          </div>
          {openSections ? (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-500" />
          )}
        </button>

        {openSections && (
          <div className="p-5 bg-white border-t border-gray-200">
            {/* Data Sources Information */}
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="font-bold text-gray-800 mb-2">{fuelFactorsInfo.title}</h3>
              <p className="text-gray-600 text-sm mb-3">{fuelFactorsInfo.description}</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {fuelFactorsInfo.sources.map((source, index) => (
                  <div key={index} className="p-3 bg-white rounded border border-blue-100">
                    <h4 className="font-semibold text-gray-700 text-sm mb-2">{source.name}</h4>
                    <p className="text-gray-600 text-xs mb-2">{source.factors}</p>
                    <p className="text-gray-500 text-xs italic">{source.purpose}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Emission Calculations */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {emissionOverview.map((item) => (
                <div
                  key={item.id}
                  className={`p-5 rounded-lg border ${item.borderColor} ${item.bgColor}`}
                >
                  <h4 className="font-semibold text-gray-800 mb-3">
                    {item.title}
                  </h4>
                  
                  <div className="mb-3">
                    <p className="text-sm font-medium text-gray-700 mb-1">Formula:</p>
                    <div className="p-2 bg-white rounded border border-gray-200">
                      {item.formulas ? (
                        item.formulas.map((formula, index) => (
                          <p
                            key={index}
                            className={`font-mono text-sm ${item.textColor} ${index > 0 ? 'mt-1' : ''}`}
                          >
                            {formula}
                          </p>
                        ))
                      ) : (
                        <p className={`font-mono text-sm ${item.textColor}`}>
                          {item.formula}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="mb-3">
                    <p className="text-sm font-medium text-gray-700 mb-1">Calculation:</p>
                    <p className={`text-sm ${item.textColor} italic`}>
                      {item.calculation}
                    </p>
                  </div>

                  {item.calculationDetails && (
                    <div className="mb-3">
                      <p className="text-sm font-medium text-gray-700 mb-1">Details:</p>
                      <ul className="text-xs text-gray-600 space-y-1">
                        {item.calculationDetails.map((detail, index) => (
                          <li key={index}>• {detail}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="mb-3">
                    <p className="text-sm font-medium text-gray-700 mb-1">Description:</p>
                    <p className="text-sm text-gray-600">
                      {item.description}
                    </p>
                  </div>

                  {item.additionalInfo && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <p className="text-sm font-medium text-gray-700 mb-1">Additional Information:</p>
                      <p className="text-sm text-gray-600 italic">
                        {item.additionalInfo}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VesselEmissionOverview;